import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { endpoints } from "../endpoints";
import { Banner } from "../../models/Banners";
import { resolveBannerUrl } from "../../utils/bannerUrl";

export const publishedBannerQueryKey = ["banner", "published"] as const;

function preloadBannerImage(url: string | undefined) {
  const resolved = resolveBannerUrl(url);
  if (!resolved) {
    return;
  }
  const img = new Image();
  img.src = resolved;
}

export async function fetchPublishedBanner(): Promise<Banner | null> {
  const response = await axios.get<Banner[]>(endpoints.getBanner);
  const banners = Array.isArray(response.data) ? response.data : [];
  const published = banners.find((banner) => banner.status === "published") ?? null;
  if (published?.url) {
    preloadBannerImage(published.url);
  }
  return published;
}

export function usePublishedBanner() {
  return useQuery({
    queryKey: publishedBannerQueryKey,
    queryFn: fetchPublishedBanner,
    staleTime: 5 * 60 * 1000,
  });
}

/** Start banner metadata + image fetch as soon as the user session is active. */
export function usePrefetchPublishedBanner(userId: string | number | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) {
      return;
    }
    void queryClient.prefetchQuery({
      queryKey: publishedBannerQueryKey,
      queryFn: fetchPublishedBanner,
      staleTime: 5 * 60 * 1000,
    });
  }, [userId, queryClient]);
}

export function useInvalidatePublishedBanner() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: publishedBannerQueryKey });
}
